// Fibre Distribution — read API for the outside fibre plant
// (PSDP / SDP / UDP / FCD / PUDP nodes, F-numbered cables, spans, joints).
// Go side: internal/fiber. Admin/support only; resellers are 403'd at the
// chokepoint (resellerCan doesn't list /v1/api/fiber/*).

import type { ApiClient } from "./client.js";

export type DPKind = "psdp" | "sdp" | "udp" | "fcd" | "pudp" | "split" | "inline_1to2";

export interface DistPoint {
  id: number;
  code: string;
  kind: DPKind | string;
  split_ratio?: number;
  notes?: string;
  upstream?: DPUpstream;
}

/** "What feeds this DP?" — the source (PON or upstream DP) of the first
 *  inbound span, plus the cable + colour of the final hop that hands
 *  off into this DP. nil/undefined when the DP has no inbound span. */
export interface DPUpstream {
  from: string;             // "GPON1 / PON 12" or upstream DP code
  is_pon?: boolean;
  olt_name?: string;        // for pon_to_dp, the OLT registration name
  pon_port?: number;        // for pon_to_dp, the port number
  olt_port_alias?: string;  // live OLT-side ifDescr (set on the OLT)
  hops?: DPUpstreamHop[];
  total_length_m?: number;
}

/** One entry in the cable chain feeding a DP. Either `cable` is set
 *  (a registered F-code at `core_index`) or `is_short` is true (an
 *  inline short-cable jumper not in the registry). */
export interface DPUpstreamHop {
  seq: number;
  cable?: string;
  brand?: string;
  batch_code?: string;
  length_m?: number;
  core_index?: number;
  colour?: string;
  is_short?: boolean;
  cable_from?: string;
  cable_to?: string;
}

export interface CableSummary {
  id: number;
  code: string;
  core_count: number;
  from_dp?: string;
  from_dp_id?: number;
  to_dp?: string;
  to_dp_id?: number;
  from_odf?: string;
  from_odf_id?: number;
  length_m?: number;
  brand?: string;
  batch_code?: string;
  installed_at?: string;
  notes?: string;
}

export interface CableJoint {
  id: number;
  position_m?: number;
  joint_type: string;
  patched_in_brand?: string;
  patched_in_batch?: string;
  patched_in_length_m?: number;
  noticed_at?: string;
  repaired_at?: string;
  notes?: string;
}

export interface CableUsage {
  core_index: number;
  colour: string;
  span_id?: number;
  span_from?: string;
  span_to?: string;
}

export interface CableDetail extends CableSummary {
  joints: CableJoint[];
  usage: CableUsage[];
}

export interface SpanHop {
  seq: number;
  cable_id?: number;
  cable?: string;
  core_index?: number;
  colour?: string;
  length_m?: number;
  /** The cable's PHYSICAL endpoints from the fiber_cables registry.
   *  When the next hop is a different cable, the splice between them
   *  lives at this hop's `to_dp` (== the next hop's `from_dp`) — usually
   *  a named FCD. Lets the schematic anchor splices at real DPs instead
   *  of synthetic joints. */
  from_dp_id?: number;
  from_dp_code?: string;
  from_dp_kind?: string;
  to_dp_id?: number;
  to_dp_code?: string;
  to_dp_kind?: string;
  short?: boolean;
  short_length_m?: number;
  short_ratio?: number;
  notes?: string;
}

export interface SpanDetail {
  id: number;
  kind: "pon_to_dp" | "dp_to_dp" | string;
  olt?: string;
  olt_id?: number;
  pon_port?: number;
  from_dp?: string;
  from_dp_id?: number;
  from_odf?: string;
  to_dp: string;
  to_dp_id: number;
  hops: SpanHop[];
  total_length_m?: number;
  notes?: string;
}

export interface PONListing {
  olt_id: number;
  olt_name: string;
  pon_port: number;
  to_dp_id: number;
  to_dp_code: string;
  to_dp_kind: string;
  hop_summary?: string;
}

export interface TreeNode {
  id: string;
  kind: string;
  label: string;
  split_ratio?: number;
  used?: number;
  closed?: number;
  dp_id?: number;
}

export interface TreeEdge {
  id: string;
  source: string;
  target: string;
  span_id: number;
  hops: SpanHop[];
  length_m?: number;
}

export interface CapacityRow {
  dp_id: number;
  code: string;
  kind: string;
  ratio: number;
  used: number;
  closed: number;
  free: number;
  pct_used?: number;
  upstream_dp?: string;
}

export interface FiberIssue {
  id: number;
  source: "import" | "live";
  kind: string;
  where: string;
  detail: string;
  severity: "error" | "warning" | "info";
  refreshed_at: string;
}

export interface StateResp {
  ok: boolean;
  counts?: Record<string, number>;
  error?: string;
}
export interface DistPointsResp {
  ok: boolean;
  rows?: DistPoint[];
  count?: number;
  dist_point?: DistPoint;
  error?: string;
}
export interface CablesResp {
  ok: boolean;
  rows?: CableSummary[];
  count?: number;
  cable?: CableDetail;
  error?: string;
}
export interface SpanResp {
  ok: boolean;
  span?: SpanDetail;
  error?: string;
}
export interface PonsResp {
  ok: boolean;
  rows?: PONListing[];
  count?: number;
  error?: string;
}
export interface TreeResp {
  ok: boolean;
  olt?: string;
  pon?: number;
  nodes?: TreeNode[];
  edges?: TreeEdge[];
  error?: string;
}
export interface CapacityResp {
  ok: boolean;
  rows?: CapacityRow[];
  count?: number;
  error?: string;
}
export interface IssuesResp {
  ok: boolean;
  rows?: FiberIssue[];
  count?: number;
  error?: string;
}

export interface ODFRow {
  id: number;
  code: string;
  name?: string;
}

export interface ODFsResp {
  ok: boolean;
  rows?: ODFRow[];
  count?: number;
  error?: string;
}

/** Payload for /v1/api/fiber/odfs/save. id=0 inserts, id>0 updates.
 *  Used by the Nodes page when the row's kind is "odf" (the row lives
 *  in fiber_odfs, not fiber_dist_points). */
export interface ODFSavePayload {
  id: number;
  code: string;
  name: string;
  lat: number | null;
  lng: number | null;
  notes: string;
}

export interface ODFSaveResp {
  ok: boolean;
  id?: number;
  error?: string;
}

export interface ODFDeleteResp {
  ok: boolean;
  error?: string;
}

/** Payload for /v1/api/fiber/dist_points/save. `id=0` inserts a new
 *  DP (code required); `id>0` updates an existing one (code read-only —
 *  renames cascade through spans + RADIUS and need their own action).
 *  `split_ratio`, `parent_odf_id`, `lat`, `lng` may be null to clear. */
export interface DPSavePayload {
  id: number;
  code?: string;
  kind: DPKind | string;
  split_ratio: number | null;
  parent_odf_id: number | null;
  lat: number | null;
  lng: number | null;
  notes: string;
}

export interface DPSaveResp {
  ok: boolean;
  dist_point?: DistPoint;
  error?: string;
}

/** "What references this DP?" — used to warn before delete. Sets nulls
 *  on most FKs; spans where this DP is the destination cascade-delete. */
export interface DPRefs {
  spans_to: number;
  spans_from: number;
  cables_from: number;
  cables_to: number;
  drops: number;
  subscribers: number;
}

export interface DPRefsResp {
  ok: boolean;
  refs?: DPRefs;
  code?: string;
  error?: string;
}

export interface DPDeleteResp {
  ok: boolean;
  subscribers?: number;
  error?: string;
}

/** Payload for /v1/api/fiber/cables/save. id=0 inserts, id>0 updates. */
export interface CableSavePayload {
  id: number;
  code: string;
  core_count: number;
  from_dp_id: number | null;
  to_dp_id: number | null;
  from_odf_id: number | null;
  length_m: number | null;
  brand: string;
  batch_code: string;
  installed_at: string; // "YYYY-MM-DD" or ""
  notes: string;
}

export interface CableSaveResp {
  ok: boolean;
  id?: number;
  error?: string;
}

/** Payload for /v1/api/fiber/spans/save. id=0 inserts, id>0 replaces
 *  (existing hops are deleted and the new list is inserted verbatim,
 *  so reorders / removals are atomic). */
export interface SpanSavePayload {
  id: number;
  kind: "pon_to_dp" | "dp_to_dp";
  olt_id: number | null;
  pon_port: number | null;
  from_dp_id: number | null;
  to_dp_id: number;
  notes: string;
  hops: SpanHopSaveItem[];
}

export interface SpanHopSaveItem {
  seq: number;
  cable_id: number | null;
  core_index: number | null;
  short_length_m: number | null;
  short_ratio: number | null;
  notes: string;
}

export interface SpanSaveResp {
  ok: boolean;
  id?: number;
  error?: string;
}

export interface SpanDeleteResp {
  ok: boolean;
  error?: string;
}

/** Payload for /v1/api/fiber/joints/save. id=0 inserts (cable_id
 *  required), id>0 updates in place. cable_id is immutable on update —
 *  delete + recreate to move a joint to a different cable. */
export interface JointSavePayload {
  id: number;
  cable_id: number;
  position_m: number | null;
  joint_type: "cut_repair" | "mid_span_split" | "inspection";
  patched_in_brand: string;
  patched_in_batch: string;
  patched_in_length_m: number | null;
  noticed_at: string;  // "YYYY-MM-DD" or ""
  repaired_at: string; // "YYYY-MM-DD" or ""
  notes: string;
}

export interface JointSaveResp {
  ok: boolean;
  id?: number;
  error?: string;
}

export interface JointDeleteResp {
  ok: boolean;
  error?: string;
}

/** A row of onu_drops as returned by GET /v1/api/fiber/drops?username=N.
 *  `connected_at_dp_id` carries the FK; `connected_at_code` is the
 *  resolved DP name for display. */
export interface DropRow {
  username: string;
  connected_at_dp_id?: number;
  connected_at_code?: string;
  port_index?: number;
  drop_length_m?: number;
  drop_brand?: string;
  drop_batch_code?: string;
  onu_paid_by?: "user" | "office" | "user_supplied" | "";
  connection_fee?: number;
  technicians?: string;
  connected_on?: string;
  notes?: string;
  updated_at?: string;
}

export interface DropGetResp {
  ok: boolean;
  drop: DropRow | null;
  error?: string;
}

/** Payload for /v1/api/fiber/drops/save. Upsert by `username`. */
export interface DropSavePayload {
  username: string;
  connected_at_dp_id: number | null;
  port_index: number | null;
  drop_length_m: number | null;
  drop_brand: string;
  drop_batch_code: string;
  onu_paid_by: "user" | "office" | "user_supplied" | "";
  connection_fee: number | null;
  technicians: string;
  connected_on: string;
  notes: string;
}

export interface DropSaveResp {
  ok: boolean;
  username?: string;
  error?: string;
}

export interface DropDeleteResp {
  ok: boolean;
  error?: string;
}

export function makeFiberApi(client: ApiClient) {
  return {
    /** Row counts of every fibre table — cheap heartbeat for the import flow. */
    state(): Promise<StateResp> {
      return client.get<StateResp>("/v1/api/fiber/state");
    },
    /** Distribution points: list (filterable by kind / free-text q) or one (?id). */
    distPoints(filters: { id?: number; kind?: string; q?: string; limit?: number } = {}): Promise<DistPointsResp> {
      return client.get<DistPointsResp>("/v1/api/fiber/dist_points", {
        id: filters.id,
        kind: filters.kind,
        q: filters.q,
        limit: filters.limit,
      });
    },
    /** Cables: list or one (?id) — single returns joints + per-core usage. */
    cables(filters: { id?: number; q?: string; brand?: string; limit?: number } = {}): Promise<CablesResp> {
      return client.get<CablesResp>("/v1/api/fiber/cables", {
        id: filters.id,
        q: filters.q,
        brand: filters.brand,
        limit: filters.limit,
      });
    },
    /** Span by id (with hop chain). No list — spans are browsed via /pons + /tree. */
    span(id: number): Promise<SpanResp> {
      return client.get<SpanResp>("/v1/api/fiber/spans", { id });
    },
    /** Every provisioned PON port — the schematic's entry-point list. */
    pons(): Promise<PonsResp> {
      return client.get<PonsResp>("/v1/api/fiber/pons");
    },
    /** Subtree below one PON port — React-Flow ready nodes + edges. */
    tree(olt: string, pon: number): Promise<TreeResp> {
      return client.get<TreeResp>("/v1/api/fiber/tree", { olt, pon });
    },
    /** UDP fill-up dashboard — ratio + live used/closed + upstream parent. */
    capacity(): Promise<CapacityResp> {
      return client.get<CapacityResp>("/v1/api/fiber/capacity");
    },
    /** Fibre data-quality findings — filterable by source (import|live) + severity. */
    issues(filters: { source?: string; severity?: string; limit?: number } = {}): Promise<IssuesResp> {
      return client.get<IssuesResp>("/v1/api/fiber/issues", {
        source: filters.source,
        severity: filters.severity,
        limit: filters.limit,
      });
    },
    /** List of ODFs — dropdown source for the DP edit dialog. */
    odfs(): Promise<ODFsResp> {
      return client.get<ODFsResp>("/v1/api/fiber/odfs");
    },
    /** Add or edit one ODF. Admin OR support. */
    saveODF(body: ODFSavePayload): Promise<ODFSaveResp> {
      return client.post<ODFSaveResp>("/v1/api/fiber/odfs/save", body);
    },
    /** Delete one ODF (DP / cable FKs are SET NULL). Admin OR support. */
    deleteODF(id: number): Promise<ODFDeleteResp> {
      return client.post<ODFDeleteResp>("/v1/api/fiber/odfs/delete", { id });
    },
    /** Save edits to one distribution point. Admin OR support. */
    saveDP(body: DPSavePayload): Promise<DPSaveResp> {
      return client.post<DPSaveResp>("/v1/api/fiber/dist_points/save", body);
    },
    /** Inspect what references one DP — counts of spans / cables / drops /
     *  subscribers. Powers the delete confirmation. */
    dpRefs(id: number): Promise<DPRefsResp> {
      return client.get<DPRefsResp>("/v1/api/fiber/dist_points/refs", { id });
    },
    /** Delete one DP. Refused server-side if RADIUS subscribers still
     *  use the code. Admin OR support. */
    deleteDP(id: number): Promise<DPDeleteResp> {
      return client.post<DPDeleteResp>("/v1/api/fiber/dist_points/delete", { id });
    },
    /** Add or edit one cable. Admin OR support. */
    saveCable(body: CableSavePayload): Promise<CableSaveResp> {
      return client.post<CableSaveResp>("/v1/api/fiber/cables/save", body);
    },
    /** Add or replace one span (with its hop list). Admin OR support. */
    saveSpan(body: SpanSavePayload): Promise<SpanSaveResp> {
      return client.post<SpanSaveResp>("/v1/api/fiber/spans/save", body);
    },
    /** Delete one span (and its hops via FK cascade). Admin OR support. */
    deleteSpan(id: number): Promise<SpanDeleteResp> {
      return client.post<SpanDeleteResp>("/v1/api/fiber/spans/delete", { id });
    },
    /** Add or edit one cable joint (cut/repair/inspection). Admin OR support. */
    saveJoint(body: JointSavePayload): Promise<JointSaveResp> {
      return client.post<JointSaveResp>("/v1/api/fiber/joints/save", body);
    },
    /** Delete one cable joint. Admin OR support. */
    deleteJoint(id: number): Promise<JointDeleteResp> {
      return client.post<JointDeleteResp>("/v1/api/fiber/joints/delete", { id });
    },
    /** Get the drop record for one subscriber (drop is null if none on file). */
    drop(username: string): Promise<DropGetResp> {
      return client.get<DropGetResp>("/v1/api/fiber/drops", { username });
    },
    /** Upsert one subscriber's drop record. Admin OR support. */
    saveDrop(body: DropSavePayload): Promise<DropSaveResp> {
      return client.post<DropSaveResp>("/v1/api/fiber/drops/save", body);
    },
    /** Delete the drop record for one subscriber. Admin OR support. */
    deleteDrop(username: string): Promise<DropDeleteResp> {
      return client.post<DropDeleteResp>("/v1/api/fiber/drops/delete", { username });
    },
  };
}

export type FiberApi = ReturnType<typeof makeFiberApi>;
